import { initializeChatSession, sendUserMessage } from "../services/chatIntegration";
import { sendPageToBackend, sendDetailedCheckToBackend, sendExportSummaryToBackend } from "../services/documentService";
import { submitComment } from "../services/pipelineService";
import {
  updateStatusIndicator,
  formatMarkdownToHTML,
  isComplianceData,
  formatComplianceData,
  updateComplianceScore
} from "./formatUtils";
import { showAnalysisCompletionOptions, handleDetailedCheck, handleExportSummary, selectPageNumber, selectSubpart } from "../services/analysisCompletionService";
import { detectUSPIDocument } from "../services/uspiDetectionService";
import { startRealtimeUpdates, stopRealtimeUpdates, type StepEvent } from "../services/realtimeService";


let currentSessionId: string | null = null;
let currentPage = 1;
let storedComplianceResults: string | null = null;
let commentActionsLog: Array<{itemId: string, comment: string, action: string, reviewerComment: string, section: string, timestamp: string}> = [];

//  (for real-time updates from backend)
let activeProgressRow: any = null;
let isAnalysisActive = false;
const completedSteps = new Set<string>();
const messageQueue: any[] = []; // Queue to collect messages for sorting
const displayedSuccessMessages = new Set<string>(); // Track displayed success messages to avoid duplicates
let lastProcessedSequence = -1; // Track the last sequence we've displayed
let fallbackProgressRow: any = null; // Fallback progress indicator when no updates are showing
// Idle indicator state
let lastActivityAt: number = Date.now();
let idleTimer: number | null = null;
const IDLE_DELAY_MS = 1500;


/**
 * Handle real-time step events from backend.
 */
function handleStepEvent(event: StepEvent) {
  // Add sequence field check (if not present, use timestamp-based fallback)
  const sequence = (event as any).sequence !== undefined ? (event as any).sequence : Date.now();
  
  // Queue the message for processing
  messageQueue.push({ ...event, sequence });
  console.log(`taskpane: Queued event with sequence ${sequence}, step: ${event.step}, type: ${event.type}. Queue length: ${messageQueue.length}`);
  
  // Sort queue by sequence
  messageQueue.sort((a, b) => a.sequence - b.sequence);
  
  // Process all queued messages in order that haven't been processed yet
  while (messageQueue.length > 0 && messageQueue[0].sequence >= lastProcessedSequence) {
    const queuedEvent = messageQueue.shift()!;
    lastProcessedSequence = queuedEvent.sequence;
    console.log(`taskpane: Processing queued event with sequence ${queuedEvent.sequence}, step: ${queuedEvent.step}, type: ${queuedEvent.type}`);
    
    processStepEvent(queuedEvent);
  }

  if (messageQueue.length > 0) {
    console.log(`taskpane: Queue has ${messageQueue.length} pending messages. Next sequence to process: ${messageQueue[0].sequence}, last processed: ${lastProcessedSequence}`);
  }
}

// Success messages for completed steps
const SUCCESS_MESSAGES: Record<string, string> = {
  'pages_summarized': 'Pages summarized',
  'document_summary_generated': 'Document summary generated',
  'conducting_compliance': 'Compliance analyzed',
  'summarizing_compliance': 'Analysis complete'
};

// Map success steps to their corresponding progress steps for updating the same row
const PROGRESS_STEP_FOR_SUCCESS: Record<string, string> = {
  'pages_summarized': 'summarizing_pages',
  'document_summary_generated': 'document_summarizing',
  'conducting_compliance': 'conducting_compliance',
  'summarizing_compliance': 'summarizing_compliance',
  'completed': 'conducting_compliance',
  'detailed_check': 'detailed_check_formatting'
};

// Track compliance analysis progress
let complianceProgressRow: any = null;
const complianceSteps = ['Subpart_B', 'Subpart_D', 'Subpart_E', 'Subpart_G', 'Subpart_C', 'Subpart_A', 'Subpart_F'];
let completedComplianceSteps: string[] = [];

/**
 * Check if there are pending processes and start next one if needed
 */
function checkAndStartNextProcess() {
  // Look for any queued update events that should start next
  const nextUpdate = messageQueue.find(event => event.type === 'update' && !completedSteps.has(event.step));
  if (nextUpdate && !activeProgressRow) {
    // Process the next update immediately to avoid idle time
    const index = messageQueue.indexOf(nextUpdate);
    if (index > -1) {
      messageQueue.splice(index, 1);
      processStepEvent(nextUpdate);
    }
  }
}

function processStepEvent(event: StepEvent) {
  const { type, step, text, progress } = event;
  markActivity();

  if (text && (text.includes("Initializing document processing") ||
               text.includes("Initiating request") ||
               text.includes("Loading content for page"))) {
    console.log(`taskpane: Ignoring unwanted message: ${text}`);
    return;
  }

  // Handle detailed check completion message - let it go through normal success processing
  // if (type === 'success' && text && text.includes('Detailed check completed for page')) {
  //   displaySuccessMessage(text);
  //   return;
  // }

  if (type === 'update') {
    // Don't reopen conducting_compliance if it's already completed
    if (step === 'conducting_compliance' && completedSteps.has(step)) {
      console.log(`taskpane: Ignoring update for already completed step '${step}'`);
      return;
    }
    
    if (completedSteps.has(step) && !activeProgressRow) {
      completedSteps.delete(step);
      console.log(`taskpane: Reopening completed step '${step}' for new updates`);
    }

    if (fallbackProgressRow) {
      fallbackProgressRow.remove();
      fallbackProgressRow = null;
      console.log("taskpane: Removed fallback progress indicator");
    }

    const displayText = progress 
      ? `${text} (${progress.current} of ${progress.total})`
      : text;

    if (!activeProgressRow) {
      activeProgressRow = createProgressMessage(displayText, "bot");
      console.log(`taskpane: Created progress row for step '${step}': ${displayText}`);
    } else {
      activeProgressRow.setText(displayText);
      console.log(`taskpane: Updated progress row with step '${step}': ${displayText}`);
    }
  } else if (type === 'success') {
    // Use mapped success message if available
    const successText = SUCCESS_MESSAGES[step] || text;
    const messagesToIgnore = [
      "Finalizing document analysis...",
      "completed"
    ];
    
    if (messagesToIgnore.includes(successText)) {
      console.log(`taskpane: Ignoring redundant success message: ${successText} from step: ${step}`);
      if (!completedSteps.has(step)) {
        completedSteps.add(step);
      }
      if (activeProgressRow) {
        activeProgressRow.remove();
        activeProgressRow = null;
      }
      return;
    }

    const successKey = successText;
    if (!completedSteps.has(step)) {
      completedSteps.add(step);
    }

    if (activeProgressRow) {
      if (displayedSuccessMessages.has(successKey)) {
        console.log(`taskpane: Success message already displayed: ${successText}, removing progress row`);
        activeProgressRow.remove();
      } else {
        // Mark as complete and keep in the same row with checkmark
        activeProgressRow.complete({ success: true, successText: successText, keepRow: true });
        displayedSuccessMessages.add(successKey);
      }
      activeProgressRow = null; // Clear reference so next update starts a new row
    } else {
      if (fallbackProgressRow) {
        fallbackProgressRow.remove();
        fallbackProgressRow = null;
      }

      if (!displayedSuccessMessages.has(successKey)) {
        displaySuccessMessage(successText);
        displayedSuccessMessages.add(successKey);
      }
    }
  } else if (type === 'error') {
    // Mark as completed to prevent further updates
    if (!completedSteps.has(step)) {
      completedSteps.add(step);
    }

    if (activeProgressRow) {
      activeProgressRow.remove();
      activeProgressRow = null;
    }
    displayMessage(`❌ ${text}`, "bot");
    console.error(`taskpane: Step '${step}' failed: ${text}`);
  } else if (type === 'message') {
    // Filter out redundant messages
    if (text.includes("Document summary generated") || text === "Document Summarised" || text === "All pages summarized") {
      console.log(`taskpane: Ignoring redundant message: ${text}`);
      return;
    }
    
    // Display a regular message
    displayMessage(text, "bot");
    console.log(`taskpane: Received message: ${text}`);
  }
}

Office.onReady(async (info) => {
  if (info.host === Office.HostType.Word) {
    document.getElementById("sideload-msg").style.display = "none";
    document.getElementById("app-body").style.display = "flex";

    const sendBtn = document.getElementById("send-btn") as HTMLButtonElement;
    const input = document.getElementById("chat-input") as HTMLInputElement;

    try {
      updateStatusIndicator("loading");

      const session = await initializeChatSession();
      currentSessionId = session.id;
      (window as any).currentSessionId = currentSessionId; // Keep global reference updated

      const documentHtml = session?.documentContext?.html;
      const hasDocumentContent =
        typeof documentHtml === "string" &&
        documentHtml.replace(/<[^>]*>/g, "").trim().length > 0;

      if (!(session && session.id && session.messages && hasDocumentContent)) {
        updateStatusIndicator("error", "Document not found");
        displayMessage("👋 Hi, I'm VAQA, the Vakil's AI Quality Assistant", "bot");
        displayMessage("Please open a document to begin the compliance review.", "bot");
        return;
      }

      // Backend check
      const hasBackendError = session.messages.some(msg =>
        msg.content.includes("Backend service unavailable") ||
        msg.content.includes("offline mode")
      );

      if (hasBackendError) {
        updateStatusIndicator("error", "Backend unavailable");
        displayMessage("👋 Hi, I'm VAQA", "bot");
        displayMessage(
          "⚠️ Backend service is currently unavailable. Please ensure pipeline service is running on port 8001.",
          "bot"
        );
        return;
      }

      updateStatusIndicator("connected");
      displayMessage("👋 Hi, I'm VAQA, the Vakil's AI Quality Assistant", "bot");

      const uspiProgress = createProgressMessage("Checking document");

      try {
        const uspiResult = await detectUSPIDocument();

        if (!uspiResult.isUSPI) {
          uspiProgress.complete();
          updateStatusIndicator("error", "Invalid document type");
          displayMessage("❌ This document is not a valid USPI document.", "bot");

          if (!uspiResult.hasHighlightsHeader) {
            displayMessage(
              "The document must start with 'HIGHLIGHTS OF PRESCRIBING INFORMATION'.",
              "bot"
            );
          }

          if (uspiResult.missingRequiredSections.length > 0) {
            const bulletList = uspiResult.missingRequiredSections
              .map(section => `• ${section}`)
              .join('\n');
            displayMessage(
              `Missing required sections:\n${bulletList}`,
              "bot"
            );
          }

          displayMessage("Please open a valid USPI document to proceed.", "bot");
          return;
        }

        uspiProgress.complete();
        displayMessage("✅ USPI document found", "bot");
        displayMessage(
          "Before we proceed, I'll review your document against FDA CFR 21 Labelling guidelines.",
          "bot"
        );

      } catch (err) {
        console.error("USPI detection error:", err);
        uspiProgress.complete();
        displayMessage("⚠️ Error checking document type. Please try again.", "bot");
        return;
      }
      try {
        await sendAllPagesSequentiallyOnce();
      } catch (error) {
        console.error("Failed to send pages:", error);
        updateStatusIndicator("error", "Backend unavailable");
        displayMessage(
          "⚠️ Backend service is currently unavailable. Please ensure port 8001 is active and CORS is enabled.",
          "bot"
        );
      }

    } catch (error) {
      console.error("Failed to initialize chat session:", error);
      updateStatusIndicator("error", "Failed to load document");
      displayMessage("Document not found", "bot");
    }
    if (sendBtn) sendBtn.onclick = () => handleUserMessage(input);
    if (input) {
      input.addEventListener("keypress", (e) => {
        if (e.key === "Enter") handleUserMessage(input);
      });
    }
  }
});

async function handleUserMessage(input: HTMLInputElement) {
  const message = input.value.trim();
  if (!message || !currentSessionId) return;

  displayMessage(message, "user");
  input.value = "";

  try {
    const { assistantMessage } = await sendUserMessage(currentSessionId, message);
    displayMessage(assistantMessage.content, "bot");
  } catch (error) {
    console.error("Error sending message:", error);
    const errMsg = error instanceof Error ? error.message : String(error);
    displayMessage(`Error: ${errMsg}`, "bot");
  }
}

// UPDATED FUNCTION: Now handles markdown formatting and compliance data
function displayMessage(text: string, sender: "user" | "bot", variant: "standard" | "checklist" = "standard") {
  const chatBox = document.getElementById("chat-box");

  const messageRow = document.createElement("div");
  messageRow.classList.add("message-row", sender);

  const icon = document.createElement("div");
  icon.classList.add("message-icon", sender);

  const msgBubble = document.createElement("div");
  msgBubble.classList.add("message", sender);

  if (variant === "checklist") {
    messageRow.classList.add("checklist");
    msgBubble.classList.add("checklist");
    msgBubble.innerHTML = text;
  } else {
    // Check if this is compliance data
    if (sender === "bot" && isComplianceData(text)) {
      msgBubble.classList.add("result");
      msgBubble.innerHTML = formatComplianceData(text);
      updateComplianceScore(text);
    } else {
      // Format markdown for bot messages, plain text for user messages
      if (sender === "bot") {
        msgBubble.innerHTML = formatMarkdownToHTML(text);
      } else {
        msgBubble.textContent = text;
      }
    }
  }

  if (sender === "user") {
    messageRow.appendChild(msgBubble);
    messageRow.appendChild(icon);
  } else {
    messageRow.appendChild(icon);
    messageRow.appendChild(msgBubble);
  }

  chatBox.appendChild(messageRow);
  chatBox.scrollTop = chatBox.scrollHeight;
}

  // Idle helpers
  function hideIdleIndicator() {
    if (fallbackProgressRow) {
      fallbackProgressRow.remove();
      fallbackProgressRow = null;
      console.log("taskpane: Idle indicator hidden");
    }
  }
  function ensureIdleIndicator() {
    if (!activeProgressRow && !fallbackProgressRow) {
      fallbackProgressRow = createProgressMessage("Analyzing document", "bot");
      console.log("taskpane: Idle indicator shown");
    }
  }
  function clearIdleTimer() {
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  }
  function scheduleIdleCheck() {
    clearIdleTimer();
    if (!isAnalysisActive) return; // Don't schedule idle check if no analysis is active

    idleTimer = window.setTimeout(() => {
      const idleFor = Date.now() - lastActivityAt;
      if (idleFor >= IDLE_DELAY_MS && !activeProgressRow) {
        ensureIdleIndicator();
      }
    }, IDLE_DELAY_MS);
  }
  function markActivity() {
    lastActivityAt = Date.now();
    if (activeProgressRow) hideIdleIndicator();
    scheduleIdleCheck();
  }

  // Reset UI/state for a fresh real-time Detailed Check run
  function resetRealtimeUiStateForDetailedCheck() {
    // Stop showing any existing progress
    if (activeProgressRow) {
      activeProgressRow.remove();
      activeProgressRow = null;
    }
    if (fallbackProgressRow) {
      fallbackProgressRow.remove();
      fallbackProgressRow = null;
    }

    // Prepare tracking/state so new events are processed and messages are shown
    // Do NOT clear completedSteps or displayedSuccessMessages to preserve original
    // document analysis success message ordering and suppression across runs.
    isAnalysisActive = true;
    messageQueue.length = 0;
    lastProcessedSequence = -1; // critical so new session events aren't dropped

    // Reset idle tracking
    hideIdleIndicator();
    clearIdleTimer();
    lastActivityAt = Date.now();
  }

  // NEW: progress / loading message helper (uses existing .loading-dots CSS)
  function displaySuccessMessage(text: string) {
    // Skip if already displayed
    if (displayedSuccessMessages.has(text)) {
      console.log(`taskpane: Success message already displayed: ${text}`);
      return;
    }

    const chatBox = document.getElementById("chat-box") as HTMLElement;
    const successRow = document.createElement('div');
    successRow.classList.add('message-row', 'bot', 'success-centered');

    const successBubble = document.createElement('div');
    successBubble.classList.add('message', 'bot', 'success');

    // Messages that should not have a tick
    const noTickMessages = [
      "Finalizing document analysis...",
      "Document analysis complete. Ready for compliance review."
    ];

    if (!noTickMessages.includes(text)) {
      const tick = document.createElement('span');
      tick.classList.add('tick-icon');
      tick.innerHTML = '✔';
      successBubble.appendChild(tick);
    }

    const textNode = document.createElement('span');
    textNode.textContent = text;

    successBubble.appendChild(textNode);
    successRow.appendChild(successBubble);

    chatBox.appendChild(successRow);
    displayedSuccessMessages.add(text);
    chatBox.scrollTop = chatBox.scrollHeight;
  }

  function createProgressMessage(initialText: string, sender: "bot" | "user" = "bot") {
    const chatBox = document.getElementById("chat-box") as HTMLElement;
    const messageRow = document.createElement("div");
    messageRow.classList.add("message-row", sender, "progress-row");

    let icon: HTMLDivElement | null = null;
    // Only add icon for user messages, or if explicitly requested (but for status messages we skip bot icon)
    if (sender !== "bot") {
      icon = document.createElement("div");
      icon.classList.add("message-icon", sender);
    }

    const msgBubble = document.createElement("div");
    msgBubble.classList.add("message", sender, "progress");

    // Text span
    const textSpan = document.createElement("span");
    textSpan.classList.add("progress-text");
    textSpan.textContent = initialText;

    // Animated ellipsis (3-4 dots, horizontal only)
    const dots = document.createElement("span");
    dots.classList.add("progress-ellipsis");
    dots.innerHTML = '<span>.</span><span>.</span><span>.</span><span>.</span>';

    msgBubble.appendChild(textSpan);
    msgBubble.appendChild(dots);

    if (sender === "user") {
      messageRow.appendChild(msgBubble);
      if (icon) messageRow.appendChild(icon);
    } else {
      if (icon) {
        messageRow.appendChild(icon);
      } else {
        // Align with messages that have icons (approx 40px indentation)
        messageRow.style.paddingLeft = "40px";
      }
      messageRow.appendChild(msgBubble);
    }

    chatBox.appendChild(messageRow);
    chatBox.scrollTop = chatBox.scrollHeight;

      return {
        getText: () => textSpan.textContent || "",
        setText: (t: string) => {
          textSpan.textContent = t;
          chatBox.scrollTop = chatBox.scrollHeight;
        },
        stopAnimation: () => {
          if (dots.parentNode) dots.parentNode.removeChild(dots);
        },
        /**
         * Complete the progress message.
         * Options:
         * - if called with a string, it becomes the final text and the update message is removed (no persistent message)
         * - if called with an object { success: true, successText: string, keepRow?: boolean } the update is removed and a persistent success message is added to the chat. If keepRow is true, the progress row remains visible.
         */
        complete: (finalTextOrOptions?: string | { success?: boolean; successText?: string; keepRow?: boolean }) => {
          // If keepRow is true, stop animation but keep the row visible
          if (typeof finalTextOrOptions === 'object' && finalTextOrOptions?.keepRow) {
            // Stop animation
            if (dots.parentNode) dots.parentNode.removeChild(dots);
            // If caller requested a persistent success message, update the same row
            if (finalTextOrOptions?.success) {
              const successText = finalTextOrOptions.successText || '';
              
              // Add checkmark
              const tick = document.createElement('span');
              tick.classList.add('tick-icon');
              tick.innerHTML = '✔ ';
              tick.style.color = '#28a745';
              tick.style.marginRight = '5px';
              
              // Clear existing content and add tick + text
              textSpan.innerHTML = '';
              textSpan.appendChild(tick);
              textSpan.appendChild(document.createTextNode(successText));
              
              // Optionally change class for styling
              msgBubble.classList.remove('progress');
              msgBubble.classList.add('success');
            }
          } else {
            // Remove the progress element (update messages should vanish when completed)
            if (messageRow.parentNode) messageRow.parentNode.removeChild(messageRow);

            // If caller requested a persistent success message, render it now
            if (typeof finalTextOrOptions === 'object' && finalTextOrOptions?.success) {
              const successText = finalTextOrOptions.successText || '';
              // Filter out redundant success messages
              const messagesToIgnore = [
                "Finalizing document analysis..."
              ];
              if (!messagesToIgnore.includes(successText)) {
                displaySuccessMessage(successText);
              }
            }
          }
        },
        remove: () => {
          if (messageRow.parentNode) messageRow.parentNode.removeChild(messageRow);
        }
      };
  }

(window as any).VAQAChat = {
  initializeChatSession,
  sendUserMessage,
};

// Expose idle helpers and reset function for Detailed Check flow
(window as any).scheduleIdleCheck = scheduleIdleCheck;
(window as any).clearIdleTimer = clearIdleTimer;
(window as any).hideIdleIndicator = hideIdleIndicator;
(window as any).resetRealtimeUiStateForDetailedCheck = resetRealtimeUiStateForDetailedCheck;



async function sendAllPagesSequentiallyOnce() {
  try {
    const totalPages = await Word.run(async (context: any) => {
      const bodyRange = context.document.body.getRange();
      const pages = bodyRange.pages;
      pages.load();
      await context.sync();
      return pages.items.length;
    });

    // Start real-time updates from backend if a document ID is available
    if (currentSessionId) {
      console.log("taskpane: Starting real-time updates for session", currentSessionId);
      isAnalysisActive = true;
      completedSteps.clear();
      displayedSuccessMessages.clear(); // Clear success message tracking
      messageQueue.length = 0; // Clear message queue
      lastProcessedSequence = -1; // Reset sequence tracker
      fallbackProgressRow = null; // Reset fallback progress
      lastActivityAt = Date.now();
      clearIdleTimer();
      await startRealtimeUpdates(currentSessionId, handleStepEvent);
      
      // Schedule idle indicator if nothing happens shortly
      scheduleIdleCheck();
    }

    let lastResponse: any = null;
    let allPagesSuccessful = true;

    // Send pages to backend and collect compliance data silently (no progress indicator)
    for (let page = 1; page <= totalPages; page++) {
      try {
        const response = await sendPageToBackend(page, "load_page");
        console.log(`taskpane: Page ${page} response:`, response.success);
        if (response.success) {
          // Store the last response for compliance data
          if (response.response) {
            lastResponse = response;
          }
        } else {
          displayMessage(`⚠ Error analyzing page ${page}: ${response.message}`, "bot");
          allPagesSuccessful = false;
        }
      } catch (error) {
        console.error("Error sending page to backend:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes("500")) {
          displayMessage("⚠ The server encountered an error while processing your document. Please try again later.", "bot");
        } else {
          displayMessage(`⚠ Error analyzing page ${page}: ${errorMessage}`, "bot");
        }
        allPagesSuccessful = false;
        // Early exit on critical errors
        if (page === 1) {
          return;
        }
      }

      await new Promise(resolve => setTimeout(resolve, 3000));
    }

    // Clean up any remaining progress rows (backend will send success messages via real-time events)
    if (activeProgressRow) {
      activeProgressRow.remove();
      activeProgressRow = null;
    }

    // Wait for backend response with compliance data (with timeout)
    let attempts = 0;
    const maxAttempts = 60; // 60 attempts × 1 second = 60 seconds (1 minute) max wait
    while (!lastResponse && attempts < maxAttempts) {
      console.log(`Waiting for compliance data... attempt ${attempts + 1}/${maxAttempts}`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }

    // Display compliance results
    if (lastResponse && lastResponse.response) {
      isAnalysisActive = false;
      stopRealtimeUpdates(); // Stop updates once we have the final response
      hideIdleIndicator();
      clearIdleTimer();
      const complianceData = lastResponse.response;
      try {
        const parsed = JSON.parse(complianceData);
        if (parsed.compliance_results || parsed.final_compliance_summary) {
          // Store compliance results for export functionality
          storedComplianceResults = complianceData;
          displayMessage(complianceData, "bot");
          updateComplianceScore(complianceData);
          showAnalysisCompletionOptions(displayMessage);
        } else {
          displayMessage(complianceData, "bot");
          showAnalysisCompletionOptions(displayMessage);
        }
      } catch (error) {
        // Store compliance results for export functionality
        storedComplianceResults = complianceData;
        displayMessage(complianceData, "bot");
        showAnalysisCompletionOptions(displayMessage);
      }
    } else {
      isAnalysisActive = false;
      displayMessage("No compliance data available.", "bot");
    }
  } catch (error) {
    console.error("Error in automatic page sending:", error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    isAnalysisActive = false;
    // Stop real-time updates on error
    stopRealtimeUpdates();
    hideIdleIndicator();
    clearIdleTimer();
    
    // Clean up any pending progress rows
    if (activeProgressRow) {
      activeProgressRow.remove();
      activeProgressRow = null;
    }
    
    // Show a user-friendly error message based on the error type
    if (errorMessage.includes("500")) {
      displayMessage("⚠ The server encountered an error while processing your document. This could be temporary - please try again in a few minutes.", "bot");
    } else if (errorMessage.includes("Failed to save page data")) {
      displayMessage("⚠ There was a problem saving your document data. Please check your document and try again.", "bot");
    } else {
      displayMessage("⚠ An error occurred while analyzing your document. Please try again.", "bot");
    }
    
    // Update status indicator
    updateStatusIndicator("error", "Analysis failed");
  }
}

// Make functions globally available
(window as any).handleDetailedCheck = () => handleDetailedCheck((window as any).displayMessage);
(window as any).handleExportSummary = () => handleExportSummary((window as any).displayMessage);
(window as any).selectPageNumber = (pageNumber: number) => selectPageNumber(pageNumber);
(window as any).selectSubpart = (pageNumber: number, subpart: string) => selectSubpart(pageNumber, subpart);
(window as any).currentSessionId = currentSessionId;
(window as any).handleStepEvent = handleStepEvent;

// Make stored compliance results globally accessible
(window as any).getStoredComplianceResults = () => storedComplianceResults;
(window as any).getCommentActionsLog = () => commentActionsLog;

// Store current comment context
let currentCommentContext: { itemId: string | number; action: string; comment: string; section: string } | null = null;

// Show comment box when button is clicked
(window as any).showCommentBox = (itemId: string | number, action: string, comment: string, section: string) => {
  console.log(`📝 ${action.toUpperCase()} clicked for item ${itemId}: ${comment}`);
  
  // Store context for submission
  currentCommentContext = { itemId, action, comment, section };
  
  // Hide all other comment boxes
  const allCommentBoxes = document.querySelectorAll('.comment-box');
  allCommentBoxes.forEach(box => {
    (box as HTMLElement).style.display = 'none';
  });
  
  // Show the comment box for this item
  const commentBox = document.getElementById(`comment-${itemId}`);
  if (commentBox) {
    commentBox.style.display = 'block';
    const textarea = commentBox.querySelector('.comment-input') as HTMLTextAreaElement;
    if (textarea) {
      textarea.focus();
    }
  }
};

// Handle comment OK/Cancel
(window as any).handleComment = async (itemId: string | number, action: string) => {
  const commentBox = document.getElementById(`comment-${itemId}`);
  const textarea = commentBox?.querySelector('.comment-input') as HTMLTextAreaElement;
  
  if (action === 'ok' && currentCommentContext && textarea?.value.trim()) {
    const userInput = textarea.value.trim();
    console.log(`💬 Submitting comment for item ${itemId}:`, {
      comment: currentCommentContext.comment,
      action: currentCommentContext.action,
      section: currentCommentContext.section,
      userInput
    });
    
    try {
      // Submit comment to backend
      const result = await submitComment(
        currentCommentContext.comment,
        currentCommentContext.action,
        userInput,
        currentCommentContext.section,
        currentSessionId || undefined
      );
      
      console.log(`✅ Comment submitted successfully:`, result);
      
      // Store action in local log
      commentActionsLog.push({
        itemId: String(itemId),
        comment: currentCommentContext.comment,
        action: currentCommentContext.action,
        reviewerComment: userInput,
        section: currentCommentContext.section,
        timestamp: new Date().toISOString()
      });
      console.log(`📋 Action logged. Total actions: ${commentActionsLog.length}`);
      
      // Display submitted comment in UI
      const submittedDiv = document.getElementById(`submitted-${itemId}`);
      if (submittedDiv) {
        const actionText = currentCommentContext.action === 'accept' ? 'Accepted' : 
                          currentCommentContext.action === 'reject' ? 'Rejected' : 'Ignored';
        const actionColor = currentCommentContext.action === 'accept' ? 'green' : 
                           currentCommentContext.action === 'reject' ? 'red' : 'orange';
        submittedDiv.innerHTML = `<div class="submitted-comment-content"><div style="color: ${actionColor}; font-weight: bold">Action: ${actionText}</div><div style="color: black; font-weight: bold">Comment: ${userInput}</div></div>`;
        submittedDiv.style.display = 'block';
      }
      
      // Hide buttons after comment is submitted
      const buttonsDiv = document.querySelector(`[data-item="${itemId}"]`);
      if (buttonsDiv instanceof HTMLElement) {
        buttonsDiv.style.display = 'none';
      }
      
    } catch (error) {
      console.error(`❌ Failed to submit comment:`, error);
      // Show error to user
      const submittedDiv = document.getElementById(`submitted-${itemId}`);
      if (submittedDiv) {
        submittedDiv.innerHTML = `
          <div class="submitted-comment-content error">
            <strong>Error:</strong> Failed to submit comment. Please try again.
          </div>
        `;
        submittedDiv.style.display = 'block';
      }
    }
  }
  
  // Hide comment box and clear textarea
  if (commentBox) {
    commentBox.style.display = 'none';
    if (textarea) textarea.value = '';
  }
  
  // Clear context
  currentCommentContext = null;
};